import { createFileRoute, redirect } from '@tanstack/react-router';
import * as React from 'react';
import { useSettingsScopeRedirect } from '&/hooks/use-settings-scope-redirect';

// ─── Tenant-level tab candidates ─────────────────────────────────────────────
//
// Each entry describes a tenant-level settings tab in display order with its
// OR gate: either manage or access-read-only for the governing category.
// The first candidate whose gate passes is the redirect target, guaranteeing
// the destination's own guard will not bounce the user.
//
// Shippers is omitted: its permission gate is identical to /settings/organization
// above, so the dedup loop below would never reach it.

type TenantTabCandidate = {
  anyPermissions: string[];
  to: string;
};

const TENANT_TAB_CANDIDATES: TenantTabCandidate[] = [
  { anyPermissions: ['tenants:manage', 'tenants:access-read-only'], to: '/settings/organization' },
  { anyPermissions: ['users:manage', 'users:access-read-only'], to: '/settings/users' },
  { anyPermissions: ['tenants:manage', 'tenants:access-read-only'], to: '/settings/spaces' },
  { anyPermissions: ['tenants:manage', 'tenants:access-read-only'], to: '/settings/handling-units' },
  { anyPermissions: ['tenants:manage', 'tenants:access-read-only'], to: '/settings/order-types' },
  { anyPermissions: ['tenants:manage', 'tenants:access-read-only'], to: '/settings/vehicle-templates' },
  { anyPermissions: ['tenants:manage', 'tenants:access-read-only'], to: '/settings/national-carriers' },
  { anyPermissions: ['tenants:manage', 'tenants:access-read-only'], to: '/settings/defaults' },
];

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_authenticated/settings/')({
  beforeLoad: ({ context }): void => {
    if (!context.permissions.isReady) return;

    const { hasAnyPermission } = context.permissions;

    // Deduplicate: multiple tenant-tab candidates share the same OR gate.
    // Find the first unique category gate that passes and redirect there.
    const seen = new Set<string>();
    for (const candidate of TENANT_TAB_CANDIDATES) {
      const key = candidate.anyPermissions.join(',');
      if (!seen.has(key) && hasAnyPermission(candidate.anyPermissions)) {
        throw redirect({ to: candidate.to });
      }
      seen.add(key);
    }

    // No tenant-level tab is enterable. Fall through to SettingsIndexRedirect,
    // which uses MasterContext to resolve site- or space-scoped detail pages.
    if (
      hasAnyPermission(['spaces:manage', 'spaces:access-read-only']) ||
      hasAnyPermission(['sites:manage', 'sites:access-read-only']) ||
      hasAnyPermission(['carriers:manage', 'carriers:access-read-only'])
    ) {
      return;
    }

    // No settings-eligible permission at all — redirect home as a safety net.
    throw redirect({ to: '/' });
  },
  component: SettingsIndexRedirect,
});

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Component-level redirect that reacts to MasterContext scope changes.
 * MasterContext and usePermissions are React contexts not available in `beforeLoad`,
 * so navigation is performed via `useSettingsScopeRedirect` (a shared hook that
 * calls `resolveSettingsNavigation` — the single source of truth for the cascade).
 *
 * Gate logic (OR semantics matching the destination route guards), encoded in the resolver:
 * 0. isScopeLoading → wait (isSiteOnlyScoped defaults to false during load).
 * 1. siteId + sites:manage OR sites:access-read-only → /settings/sites/$siteId
 *    (checked first so users who have a siteId but lack spaces permission
 *    are not incorrectly sent to a space detail page).
 * 2. isSiteOnlyScoped → if firstSiteId + site perms, navigate to /settings/sites/$firstSiteId;
 *    in all cases return — never fall through to space detail for site-only users.
 * 3. spaceId + (spaces:manage OR spaces:access-read-only OR tenants:manage OR tenants:access-read-only)
 *    → /settings/spaces/$spaceId  (mirrors the space detail route's OR gate).
 *
 * This component is rendered only when `beforeLoad` did not throw a redirect
 * (i.e., site/space-scoped users who need MasterContext to resolve their target).
 */
function SettingsIndexRedirect(): React.JSX.Element | null {
  useSettingsScopeRedirect({ includeAccessFallback: false });
  return null;
}
